import { read, utils } from 'xlsx';
import canvasesApi from '@/api/canvases.api';
import nodesApi from '@/api/nodes.api';
import linksApi from '@/api/links.api';
import type { Canvas } from '@/types/canvas.types';
import { NodeStatus, NodeType, type Priority } from '@/types/node.types';
import { LinkIntensity, LinkType } from '@/types/link.types';

/**
 * Import an AgeVal "plan d'actions" XLSX export and build a new StratMap canvas.
 *
 * Mapping:
 *   Thème (col 5) → Vision node (deduped by name) — Hierarchy → Objective
 *   Objectif (col 1) → Objective node (deduped by title) — Hierarchy → Activity
 *   Action (col 27) → Activity node (one per row)
 *   Responsables (col 3) + Pilotes (col 28) → Stakeholder node (deduped by
 *     person name, unified across the two roles) — Contribution → Objective/Activity
 *   Tags (col 2) → kept as StratMap tags on the Objective
 *
 * Expected sheet layout (single sheet, header on row 0):
 *   Objective columns 0..17 (repeated as long as the Objective spans multiple rows)
 *   Action columns 27..40 (one row = one action).
 */

type Row = Array<unknown>;

const COL = {
  OBJ_TITLE: 1,
  OBJ_TAGS: 2,
  OBJ_RESPONSIBLES: 3,
  OBJ_ORGS: 4,
  OBJ_THEMES: 5,
  OBJ_PRIORITY: 7,
  OBJ_PROGRESS: 8,
  OBJ_START: 9,
  OBJ_DUE: 10,
  OBJ_CLOSED: 11,
  OBJ_STATUS: 12,
  OBJ_EXPECTED: 14,
  OBJ_BUDGET: 15,
  OBJ_JH: 16,
  OBJ_COMMENTS: 17,
  ACT_TITLE: 27,
  ACT_PILOTS: 28,
  ACT_PRIORITY: 29,
  ACT_PROGRESS: 30,
  ACT_START: 31,
  ACT_DUE: 32,
  ACT_CLOSED: 33,
  ACT_STATUS: 34,
  ACT_EXPECTED: 37,
  ACT_BUDGET: 38,
  ACT_JH: 39,
  ACT_COMMENTS: 40,
} as const;

export class PlanActionImportError extends Error {}

// ---- helpers --------------------------------------------------------------

const toStr = (v: unknown): string => {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v.trim();
  return String(v).trim();
};

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const toDateStr = (v: unknown): string | null => {
  const n = toNum(v);
  if (n === null || n <= 0) return null;
  const date = new Date(Math.round((n - 25569) * 86400 * 1000));
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('fr-FR');
};

const PRIORITY_MAP: Record<string, Priority> = {
  faible: 1,
  basse: 2,
  normale: 3,
  moyenne: 3,
  importante: 4,
  haute: 4,
  critique: 5,
  urgente: 5,
};

const mapPriority = (v: unknown): Priority => {
  const key = toStr(v).toLowerCase();
  return PRIORITY_MAP[key] ?? 3;
};

const mapProgress = (v: unknown): { progress: number | null; status: NodeStatus } => {
  const key = toStr(v).toLowerCase();
  if (!key) return { progress: null, status: NodeStatus.Active };
  if (key.includes('non initié') || key === 'non initie' || key.includes('à faire'))
    return { progress: 0, status: NodeStatus.Active };
  if (key.includes('terminé') || key === 'termine' || key.includes('clôturé') || key.includes('cloture'))
    return { progress: 100, status: NodeStatus.Archived };
  if (key.includes('vérifier') || key.includes('verifier'))
    return { progress: 90, status: NodeStatus.ToReview };
  if (key.includes('suspendu') || key.includes('pause'))
    return { progress: 50, status: NodeStatus.Paused };
  if (key.includes('en cours')) return { progress: 50, status: NodeStatus.Active };
  return { progress: null, status: NodeStatus.Active };
};

const mapStatus = (statusRaw: unknown, progressStatus: NodeStatus): NodeStatus => {
  if (progressStatus !== NodeStatus.Active) return progressStatus;
  const s = toStr(statusRaw).toLowerCase();
  if (s.includes('inactif') || s.includes('archiv')) return NodeStatus.Archived;
  return NodeStatus.Active;
};

const dedupeCleanList = (raw: unknown): string[] => {
  const parts = toStr(raw)
    .split(/[\n,;]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  return Array.from(new Set(parts));
};

/** Normalized key for dedup (case-insensitive, trimmed, collapsed whitespace). */
const normKey = (s: string): string => s.trim().toLowerCase().replace(/\s+/g, ' ');

// ---- parsed shapes --------------------------------------------------------

interface ParsedAction {
  title: string;
  description: string;
  priority: Priority;
  status: NodeStatus;
  progress: number | null;
  budgetPlanned: number | null;
  effortPlannedDays: number | null;
  pilotKeys: string[]; // references into ParsedWorkbook.stakeholders
}

interface ParsedObjective {
  title: string;
  description: string;
  priority: Priority;
  status: NodeStatus;
  progress: number | null;
  budgetPlanned: number | null;
  effortPlannedDays: number | null;
  responsibleKeys: string[]; // references into ParsedWorkbook.stakeholders
  themeKeys: string[]; // references into ParsedWorkbook.visions
  tags: string[];
  actions: ParsedAction[];
}

interface ParsedWorkbook {
  objectives: ParsedObjective[];
  /** dedup key → display label (Thèmes AgeVal, each becomes a Vision node) */
  visions: Map<string, string>;
  /** dedup key → display label (Responsables ∪ Pilotes, each becomes a Stakeholder node) */
  stakeholders: Map<string, string>;
}

function buildObjectiveDescription(row: Row): string {
  const parts: string[] = [];
  const expected = toStr(row[COL.OBJ_EXPECTED]);
  const start = toDateStr(row[COL.OBJ_START]);
  const due = toDateStr(row[COL.OBJ_DUE]);
  const closed = toDateStr(row[COL.OBJ_CLOSED]);
  const comments = toStr(row[COL.OBJ_COMMENTS]);

  if (expected) parts.push(`**Résultats attendus :** ${expected}`);
  const dates: string[] = [];
  if (start) dates.push(`début ${start}`);
  if (due) dates.push(`échéance ${due}`);
  if (closed) dates.push(`clôture ${closed}`);
  if (dates.length) parts.push(`**Dates :** ${dates.join(' · ')}`);
  if (comments) parts.push(`**Commentaires :** ${comments}`);
  return parts.join('\n\n');
}

function buildActionDescription(row: Row): string {
  const parts: string[] = [];
  const expected = toStr(row[COL.ACT_EXPECTED]);
  const start = toDateStr(row[COL.ACT_START]);
  const due = toDateStr(row[COL.ACT_DUE]);
  const closed = toDateStr(row[COL.ACT_CLOSED]);
  const comments = toStr(row[COL.ACT_COMMENTS]);

  if (expected) parts.push(`**Résultats attendus :** ${expected}`);
  const dates: string[] = [];
  if (start) dates.push(`début ${start}`);
  if (due) dates.push(`échéance ${due}`);
  if (closed) dates.push(`clôture ${closed}`);
  if (dates.length) parts.push(`**Dates :** ${dates.join(' · ')}`);
  if (comments) parts.push(`**Commentaires :** ${comments}`);
  return parts.join('\n\n');
}

function parseWorkbook(buffer: ArrayBuffer): ParsedWorkbook {
  const wb = read(buffer, { type: 'array' });
  const firstName = wb.SheetNames[0];
  if (!firstName) throw new PlanActionImportError('Classeur vide');
  const ws = wb.Sheets[firstName];
  const rows = utils.sheet_to_json<Row>(ws, { header: 1, defval: null });
  if (rows.length < 2) throw new PlanActionImportError('Aucune ligne à importer');

  const objectivesMap = new Map<string, ParsedObjective>();
  const visions = new Map<string, string>();
  const stakeholders = new Map<string, string>();

  const registerVision = (label: string): string => {
    const key = normKey(label);
    if (!visions.has(key)) visions.set(key, label);
    return key;
  };

  const registerStakeholder = (label: string): string => {
    const key = normKey(label);
    if (!stakeholders.has(key)) stakeholders.set(key, label);
    return key;
  };

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    const objTitle = toStr(row[COL.OBJ_TITLE]);
    if (!objTitle) continue;

    let obj = objectivesMap.get(objTitle);
    if (!obj) {
      const themeLabels = dedupeCleanList(row[COL.OBJ_THEMES]);
      const respLabels = dedupeCleanList(row[COL.OBJ_RESPONSIBLES]);
      const tags = dedupeCleanList(row[COL.OBJ_TAGS]);
      const objProgressInfo = mapProgress(row[COL.OBJ_PROGRESS]);
      const objStatus = mapStatus(row[COL.OBJ_STATUS], objProgressInfo.status);

      obj = {
        title: objTitle,
        description: buildObjectiveDescription(row),
        priority: mapPriority(row[COL.OBJ_PRIORITY]),
        status: objStatus,
        progress: objProgressInfo.progress,
        budgetPlanned: toNum(row[COL.OBJ_BUDGET]),
        effortPlannedDays: toNum(row[COL.OBJ_JH]),
        responsibleKeys: respLabels.map(registerStakeholder),
        themeKeys: themeLabels.map(registerVision),
        tags,
        actions: [],
      };
      objectivesMap.set(objTitle, obj);
    }

    const actTitle = toStr(row[COL.ACT_TITLE]);
    if (!actTitle) continue;

    const pilotLabels = dedupeCleanList(row[COL.ACT_PILOTS]);
    const actProgressInfo = mapProgress(row[COL.ACT_PROGRESS]);
    const actStatus = mapStatus(row[COL.ACT_STATUS], actProgressInfo.status);

    obj.actions.push({
      title: actTitle,
      description: buildActionDescription(row),
      priority: mapPriority(row[COL.ACT_PRIORITY]),
      status: actStatus,
      progress: actProgressInfo.progress,
      budgetPlanned: toNum(row[COL.ACT_BUDGET]),
      effortPlannedDays: toNum(row[COL.ACT_JH]),
      pilotKeys: pilotLabels.map(registerStakeholder),
    });
  }

  if (objectivesMap.size === 0) throw new PlanActionImportError('Aucun objectif détecté');

  return {
    objectives: Array.from(objectivesMap.values()),
    visions,
    stakeholders,
  };
}

// ---- public API -----------------------------------------------------------

export interface PlanActionImportResult {
  canvas: Canvas;
  visionCount: number;
  objectiveCount: number;
  actionCount: number;
  stakeholderCount: number;
  linkCount: number;
}

export type ImportPhase =
  | 'parsing'
  | 'canvas'
  | 'visions'
  | 'objectives'
  | 'actions'
  | 'stakeholders'
  | 'links'
  | 'done';

export interface ImportProgress {
  phase: ImportPhase;
  current: number;
  total: number;
  label?: string;
}

async function mapConcurrent<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
  onDone?: (index: number, item: T) => void,
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (true) {
      const i = next++;
      if (i >= items.length) return;
      out[i] = await fn(items[i], i);
      onDone?.(i, items[i]);
    }
  };
  const workers = Array.from({ length: Math.min(limit, items.length) }, () => worker());
  await Promise.all(workers);
  return out;
}

export async function importPlanActionXLSX(
  file: File,
  workspaceId: string,
  onProgress?: (p: ImportProgress) => void,
): Promise<PlanActionImportResult> {
  onProgress?.({ phase: 'parsing', current: 0, total: 1, label: file.name });
  const buffer = await file.arrayBuffer();
  const parsed = parseWorkbook(buffer);
  onProgress?.({ phase: 'parsing', current: 1, total: 1 });

  onProgress?.({ phase: 'canvas', current: 0, total: 1 });
  const canvasName = `Plan d'actions — ${file.name.replace(/\.xlsx$/i, '')} (import ${new Date().toLocaleDateString('fr-FR')})`;
  const canvas = await canvasesApi.create({
    workspaceId,
    name: canvasName.length > 120 ? canvasName.slice(0, 117) + '…' : canvasName,
    description: `Import automatique depuis un fichier XLSX Plan d'actions le ${new Date().toLocaleString('fr-FR')}.`,
  });
  onProgress?.({ phase: 'canvas', current: 1, total: 1 });

  const GAP_X = 420;
  const GAP_Y = 160;
  const CONCURRENCY = 5;
  const VISION_Y = -320;
  const OBJECTIVE_Y = 0;

  const maxActionsPerObjective = parsed.objectives.reduce(
    (max, o) => Math.max(max, o.actions.length),
    0,
  );
  const STAKEHOLDER_Y = GAP_Y * (maxActionsPerObjective + 2);

  // Flatten actions with their parent objective index.
  interface FlatAction {
    objIdx: number;
    actIdx: number;
    data: ParsedAction;
  }
  const flatActions: FlatAction[] = [];
  parsed.objectives.forEach((obj, objIdx) => {
    obj.actions.forEach((data, actIdx) => flatActions.push({ objIdx, actIdx, data }));
  });

  const visionEntries = Array.from(parsed.visions.entries()); // [key, label]
  const stakeholderEntries = Array.from(parsed.stakeholders.entries()); // [key, label]

  // ---- Phase 1: Visions (Thèmes) ----------------------------------------
  let visionDone = 0;
  const visionNodes = await mapConcurrent(
    visionEntries,
    CONCURRENCY,
    ([, label], idx) =>
      nodesApi.create({
        canvasId: canvas.id,
        type: NodeType.Vision,
        title: label,
        status: NodeStatus.Active,
        priority: 4,
        positionX: GAP_X * idx,
        positionY: VISION_Y,
      }),
    (_, [, label]) => {
      visionDone++;
      onProgress?.({
        phase: 'visions',
        current: visionDone,
        total: visionEntries.length,
        label,
      });
    },
  );
  const visionIdByKey = new Map<string, string>();
  visionEntries.forEach(([key], idx) => visionIdByKey.set(key, visionNodes[idx].id));

  // ---- Phase 2: Objectives ----------------------------------------------
  let objDone = 0;
  const objNodes = await mapConcurrent(
    parsed.objectives,
    CONCURRENCY,
    (obj, idx) =>
      nodesApi.create({
        canvasId: canvas.id,
        type: NodeType.Objective,
        title: obj.title,
        description: obj.description || undefined,
        status: obj.status,
        priority: obj.priority,
        positionX: GAP_X * idx,
        positionY: OBJECTIVE_Y,
        budgetPlanned: obj.budgetPlanned ?? undefined,
        effortPlannedDays: obj.effortPlannedDays ?? undefined,
        progress: obj.progress ?? undefined,
      }),
    (_, obj) => {
      objDone++;
      onProgress?.({
        phase: 'objectives',
        current: objDone,
        total: parsed.objectives.length,
        label: obj.title,
      });
    },
  );

  // ---- Phase 3: Actions --------------------------------------------------
  let actDone = 0;
  const actionNodes = await mapConcurrent(
    flatActions,
    CONCURRENCY,
    ({ objIdx, actIdx, data }) =>
      nodesApi.create({
        canvasId: canvas.id,
        type: NodeType.Activity,
        title: data.title,
        description: data.description || undefined,
        status: data.status,
        priority: data.priority,
        positionX: GAP_X * objIdx,
        positionY: GAP_Y * (actIdx + 1),
        budgetPlanned: data.budgetPlanned ?? undefined,
        effortPlannedDays: data.effortPlannedDays ?? undefined,
        progress: data.progress ?? undefined,
      }),
    (_, item) => {
      actDone++;
      onProgress?.({
        phase: 'actions',
        current: actDone,
        total: flatActions.length,
        label: item.data.title,
      });
    },
  );

  // ---- Phase 4: Stakeholders (Responsables ∪ Pilotes) -------------------
  let stakeDone = 0;
  const stakeholderNodes = await mapConcurrent(
    stakeholderEntries,
    CONCURRENCY,
    ([, label], idx) =>
      nodesApi.create({
        canvasId: canvas.id,
        type: NodeType.Stakeholder,
        title: label,
        status: NodeStatus.Active,
        priority: 3,
        positionX: GAP_X * idx,
        positionY: STAKEHOLDER_Y,
      }),
    (_, [, label]) => {
      stakeDone++;
      onProgress?.({
        phase: 'stakeholders',
        current: stakeDone,
        total: stakeholderEntries.length,
        label,
      });
    },
  );
  const stakeholderIdByKey = new Map<string, string>();
  stakeholderEntries.forEach(([key], idx) =>
    stakeholderIdByKey.set(key, stakeholderNodes[idx].id),
  );

  // ---- Phase 5: All links ------------------------------------------------
  interface LinkSpec {
    source: string;
    target: string;
    type: LinkType;
    intensity: LinkIntensity;
  }
  const linkSpecs: LinkSpec[] = [];

  // Vision → Objective (Hierarchy)
  parsed.objectives.forEach((obj, objIdx) => {
    for (const themeKey of obj.themeKeys) {
      const sourceId = visionIdByKey.get(themeKey);
      if (!sourceId) continue;
      linkSpecs.push({
        source: sourceId,
        target: objNodes[objIdx].id,
        type: LinkType.Hierarchy,
        intensity: LinkIntensity.Strong,
      });
    }
  });

  // Objective → Activity (Hierarchy)
  flatActions.forEach((item, flatIdx) => {
    linkSpecs.push({
      source: objNodes[item.objIdx].id,
      target: actionNodes[flatIdx].id,
      type: LinkType.Hierarchy,
      intensity: LinkIntensity.Strong,
    });
  });

  // Stakeholder → Objective (Contribution, via responsibles)
  parsed.objectives.forEach((obj, objIdx) => {
    for (const respKey of obj.responsibleKeys) {
      const sourceId = stakeholderIdByKey.get(respKey);
      if (!sourceId) continue;
      linkSpecs.push({
        source: sourceId,
        target: objNodes[objIdx].id,
        type: LinkType.Contribution,
        intensity: LinkIntensity.Strong,
      });
    }
  });

  // Stakeholder → Activity (Contribution, via pilots)
  flatActions.forEach((item, flatIdx) => {
    for (const pilotKey of item.data.pilotKeys) {
      const sourceId = stakeholderIdByKey.get(pilotKey);
      if (!sourceId) continue;
      linkSpecs.push({
        source: sourceId,
        target: actionNodes[flatIdx].id,
        type: LinkType.Contribution,
        intensity: LinkIntensity.Strong,
      });
    }
  });

  let linkDone = 0;
  await mapConcurrent(
    linkSpecs,
    CONCURRENCY,
    (spec) =>
      linksApi.create({
        canvasId: canvas.id,
        sourceNodeId: spec.source,
        targetNodeId: spec.target,
        type: spec.type,
        intensity: spec.intensity,
      }),
    () => {
      linkDone++;
      onProgress?.({
        phase: 'links',
        current: linkDone,
        total: linkSpecs.length,
      });
    },
  );

  onProgress?.({ phase: 'done', current: 1, total: 1 });
  return {
    canvas,
    visionCount: visionEntries.length,
    objectiveCount: parsed.objectives.length,
    actionCount: flatActions.length,
    stakeholderCount: stakeholderEntries.length,
    linkCount: linkSpecs.length,
  };
}
